/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  FlagButton Container
 */

import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { type AnalyticsC, trackGAEvent } from '@zcode/nr-utils'
import { memoMapDispatchToProps } from '@zcode/react-utils'
import {
  type Building,
  type Listing,
  MODAL_TYPE,
  type NormalListingData,
  type State,
} from '@zcode/state-type'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { addErrorButterBar } from '../../actions/butter.action'
import { isEntityFlagged } from '../../inspectors/index'
import { receiveData } from '../../modules/modals/actions/flagModal'
import { openModal } from '../../modules/modals/thunks/index'
import { FlagButton } from './FlagButton'

interface OwnProps {
  analyticChannel: typeof AnalyticsC.FULL_DETAIL | typeof AnalyticsC.PERMANENT_ADDRESS
  entity: Building | Listing<NormalListingData>
}

const mapStateToProps = (state: State, ownProps: OwnProps) => ({
  isFlagged: isEntityFlagged(state, ownProps.entity),
  label: `flag ${ownProps.entity.headerText || ''}`,
  isMobile: selectIsMobile(state),
})

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      dispatch((_, getState) => {
        const { analyticChannel, entity } = ownProps
        const state = getState()
        const isFlagged = isEntityFlagged(state, entity)
        if (isFlagged) {
          dispatch(addErrorButterBar({ message: 'You have already reported this property.' }))
        } else {
          analyticChannel && trackGAEvent(analyticChannel, 'Flag Listing', 'open')
          dispatch(receiveData({ analyticChannel, entity }))
          dispatch(openModal(MODAL_TYPE.FLAG))
        }
      })
    },
  }
}

const equalProps = ['analyticChannel' as const, 'entity' as const]
const memoMapFlagButtonDispatchToProps = memoMapDispatchToProps(mapDispatchToProps, equalProps)

export const FlagButtonContainer = connect(
  mapStateToProps,
  memoMapFlagButtonDispatchToProps
)(FlagButton)
